import { Stack } from '@mui/material';

import { AdminDashboardUserStatus, KycStatus } from '@/api/admin/types';
import {
  Badge,
  BadgeGroupCell,
  BadgeItem,
  TableColumn,
  TextCell,
  TIME_CELL_MIN_WIDTH,
  TimeCell,
} from '@/design-system';
import { formatDefaultFractionTwo } from '@/utils/numbers/formatters';

import { UserTabNameCell } from './components/user-tab-name-cell';
import { UserTabTableRow } from './components/user-tab-table.utils';
import { UserTabTableActions } from './components/user-tab-table-actions';
import { UserTabTableStatus } from './components/user-tab-table-status';

export const userStatusBadge: Record<KycStatus, BadgeItem> = {
  [KycStatus.Pending]: {
    label: 'Pending',
    color: 'orange',
    tooltip:
      'This user has received an invitation email, but has not created their Gamma Prime profile yet.',
  },
  [KycStatus.Approved]: {
    label: 'Verified',
    color: 'primary',
    tooltip: `This is a fully verified user that has completed the KYC and the Investor's Questionnaire.`,
  },
  [KycStatus.Rejected]: {
    label: 'Rejected',
    color: 'destructive',
    tooltip: 'This user has been rejected or failed KYC verification.',
  },
  [KycStatus.InProgress]: {
    label: 'In Progress',
    color: 'secondary',
    tooltip: 'This user has registered but has not yet completed KYC or their profile.',
  },
};

export const adminUserStatusBadge: Record<AdminDashboardUserStatus, BadgeItem> = {
  [AdminDashboardUserStatus.Pending]: {
    label: 'Pending',
    color: 'orange',
    tooltip:
      'This user has received an invitation email, but has not created their Gamma Prime profile yet.',
  },
  [AdminDashboardUserStatus.SignedUp]: {
    label: 'Signed Up',
    color: 'secondary',
    tooltip: 'This user has registered but has not yet completed KYC or their profile.',
  },
  [AdminDashboardUserStatus.WaitingForApproval]: {
    label: 'Waiting for Approval',
    color: 'orange',
    tooltip:
      'This user has registered and has an entity affiliation, but their profile is not yet complete.',
  },
  [AdminDashboardUserStatus.VerifiedBasic]: {
    label: 'Verified',
    color: 'primary',
    tooltip: `This is a fully verified user with no entity membership.`,
  },
  [AdminDashboardUserStatus.VerifiedEntity]: {
    label: 'Verified',
    color: 'primary',
    tooltip: `This is a fully verified user and entity member.`,
  },
  [AdminDashboardUserStatus.Blocked]: {
    label: 'Blocked',
    color: 'default',
  },
};

export const getAdminUserStatusBadges = (statuses: AdminDashboardUserStatus[]): BadgeItem[] =>
  statuses
    .filter((status) => status !== AdminDashboardUserStatus.Blocked)
    .map((status) => adminUserStatusBadge[status]);

export const usersTableColumns: TableColumn<UserTabTableRow>[] = [
  {
    key: 'name',
    header: 'Full Name',
    width: '1.7fr',
    minWidth: 180,
    maxWidth: 320,
    sortable: true,
    cell: (row) => <UserTabNameCell name={row.nameWithAlias} tags={row.tags} />,
  },
  {
    key: 'email',
    header: 'Email address',
    width: '2fr',
    sortable: true,
    cell: (row) => (
      <TextCell title={row.email} tooltip={row.email.length > 32 ? row.email : undefined} />
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: '1.5fr',
    sortable: true,
    cell: ({ statuses }) => <UserTabTableStatus statuses={statuses} />,
  },
  {
    key: 'entityAffiliation',
    header: 'Entity Affiliation',
    width: '1fr',
    cell: (row) => <TextCell title={row.entityAffiliation ? 'Yes' : 'No'} />,
  },
  {
    key: 'isGammaTeam',
    header: 'Gamma Prime Team',
    width: '1fr',
    cell: (row) => <TextCell title={row.isGammaTeam ? 'Yes' : 'No'} />,
  },
  {
    key: 'currentIntroducer',
    header: 'Current Introducer',
    width: '1.5fr',
    cell: (row) => {
      if (!row.currentIntroducer) return <TextCell title="-" />;
      const { introducerName, attributionSource } = row.currentIntroducer;
      return (
        <Stack flexDirection="row" alignItems="center" gap="6px">
          <TextCell title={introducerName ?? '-'} />
          <Badge
            label={attributionSource === 'manual' ? 'Manual' : 'Auto'}
            color={attributionSource === 'manual' ? 'orange' : 'secondary'}
            variant="subtle"
          />
        </Stack>
      );
    },
  },
  {
    key: 'countryOfResidence',
    header: 'Country of Residence',
    width: '1fr',
    cell: (row) => <TextCell title={row.countryOfResidence || '-'} />,
  },
  {
    key: 'city',
    header: 'City',
    width: '1fr',
    cell: (row) => <TextCell title={row.city || '-'} />,
  },
  {
    key: 'phoneNumber',
    header: 'Phone Number',
    width: '1fr',
    cell: (row) => <TextCell title={row.phoneNumber || '-'} />,
  },
  {
    key: 'nationalities',
    header: 'Citizenship / Nationality',
    width: '1fr',
    cell: (row) => <TextCell title={row.nationalities.join(', ')} />,
  },
  {
    key: 'isUsPerson',
    header: 'US Person',
    width: '1fr',
    cell: (row) => <TextCell title={row.isUsPerson ? 'Yes' : 'No'} />,
  },
  {
    key: 'isPoliticallyExposedPerson',
    header: 'Politically Exposed Person',
    width: '1fr',
    cell: (row) => <TextCell title={row.isPoliticallyExposedPerson ? 'Yes' : 'No'} />,
  },
  {
    key: 'businessOrOccupation',
    header: 'Business / Occupation',
    width: '1fr',
    cell: (row) => <TextCell title={row.businessOrOccupation || '-'} />,
  },
  {
    key: 'netWorth',
    header: 'Net Worth',
    width: '1fr',
    cell: (row) => <TextCell title={row.netWorth || '-'} />,
  },
  {
    key: 'householdGrossIncome',
    header: 'Household Gross Income',
    width: '1fr',
    cell: (row) => <TextCell title={row.householdGrossIncome || '-'} />,
  },
  {
    key: 'fundsInvestedCount',
    header: '# Fund Invested',
    width: '1fr',
    cell: (row) => <TextCell title={String(row.fundsInvestedCount)} />,
  },
  {
    key: 'totalAmountInvestedUsd',
    header: 'Amount Invested ($)',
    width: '1fr',
    cell: (row) => <TextCell title={`$${formatDefaultFractionTwo(row.totalAmountInvestedUsd)}`} />,
  },
  {
    key: 'isIntroducer',
    header: 'Introducer',
    width: '1fr',
    cell: (row) => <TextCell title={row.isIntroducer ? 'Yes' : 'No'} />,
  },
  {
    key: 'questionnaireSubmitted',
    header: 'Questionnaire Submitted',
    width: '1fr',
    cell: (row) => <TextCell title={row.questionnaireSubmitted ? 'Yes' : 'No'} />,
  },
  {
    key: 'kycStatus',
    header: 'KYC Status',
    width: '1fr',
    cell: (row) =>
      row.kycStatus ? (
        <BadgeGroupCell badges={[userStatusBadge[row.kycStatus]]} />
      ) : (
        <TextCell title="-" />
      ),
  },
  {
    key: 'verifiedAt',
    header: 'Verified Since',
    width: '1.36fr',
    minWidth: TIME_CELL_MIN_WIDTH,
    sortable: true,
    cell: (row) =>
      row.verifiedSince ? <TimeCell date={row.verifiedSince} /> : <TextCell title="-" />,
  },
  {
    key: 'actions',
    header: 'Actions',
    width: '180px',
    align: 'right',
    pinned: true,
    cell: ({
      id,
      nameWithAlias,
      email,
      statuses,
      isGammaTeam,
      invitationType,
      invitationTypes,
    }) => (
      <UserTabTableActions
        id={id}
        nameWithAlias={nameWithAlias}
        email={email}
        statuses={statuses}
        isGammaTeam={isGammaTeam}
        invitationType={invitationType}
        invitationTypes={invitationTypes}
      />
    ),
  },
];
